import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { AccountBalance, Bolt, CheckCircle, PlayArrow, Close, ContentCopy, ErrorOutline, HourglassTop, Refresh } from '@mui/icons-material';
import { Grid } from '../components/common/TypedGrid';
import { FilterTabs, PageHeader, ResponsiveColumn, ResponsiveTable, SearchField, SectionCard, StatCard, StatusChip } from '../components/common';
import { useAdmin } from '../contexts/AdminContext';
import { callableError } from '../services/agencyAdminService';
import withdrawalAdminService, { AdminWithdrawal, PayMethod, PayoutConfig, WithdrawalAction, WithdrawalStatus } from '../services/withdrawalAdminService';

type Filter = 'pending' | 'processing' | 'failed' | 'paid' | 'rejected' | 'all';

const KIND_LABEL: Record<AdminWithdrawal['kind'], string> = { individual: 'Partner', agency: 'Agency', customer: 'Customer' };
const KIND_COLOR: Record<AdminWithdrawal['kind'], 'primary.main' | 'secondary.main' | 'info.main'> = { individual: 'primary.main', agency: 'secondary.main', customer: 'info.main' };

const inr = (n?: number) => `₹${(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const when = (d?: Date | null) => (d ? d.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

/** Mirrors allowedActions() in functions/src/lib/withdrawals.ts — the server has the final say. */
function actionsFor(w: AdminWithdrawal, razorpayx: boolean): WithdrawalAction[] {
  switch (w.status) {
    case 'pending':
      return razorpayx ? ['payout', 'approve', 'mark_paid', 'reject'] : ['approve', 'mark_paid', 'reject'];
    case 'failed':
      return razorpayx ? ['payout', 'mark_paid', 'reject'] : ['mark_paid', 'reject'];
    case 'processing':
      if (w.payoutId) return ['refresh'];
      if (w.manual) return ['mark_paid', 'reject'];
      return razorpayx ? ['payout', 'refresh'] : ['refresh'];
    default:
      return [];
  }
}

/**
 * Withdrawals: partners and agencies asking for their earnings, and customers
 * taking money out of their wallet. The money is already set aside in their
 * wallet; pay it (bank transfer + "Mark paid", or "Pay now" through RazorpayX)
 * or reject it and it goes back to their wallet.
 */
const Withdrawals: React.FC = () => {
  const { effectiveCityId, isSuperAdmin } = useAdmin();
  const [rows, setRows] = useState<AdminWithdrawal[]>([]);
  const [config, setConfig] = useState<PayoutConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>('pending');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dialog, setDialog] = useState<'mark_paid' | 'reject' | 'payout' | null>(null);
  const [utr, setUtr] = useState('');
  const [method, setMethod] = useState<PayMethod>('bank_transfer');
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');
  const [acting, setActing] = useState(false);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' | 'info' } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, cfg] = await Promise.all([withdrawalAdminService.list(effectiveCityId), withdrawalAdminService.config().catch(() => null)]);
      setRows(list);
      setConfig(cfg);
    } catch (e) {
      setToast({ msg: callableError(e), severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [effectiveCityId]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    rows.forEach((w) => (c[w.status] = (c[w.status] || 0) + 1));
    return c;
  }, [rows]);
  const owed = useMemo(() => rows.filter((w) => ['pending', 'processing', 'failed'].includes(w.status)).reduce((n, w) => n + w.amount, 0), [rows]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(
      (w) =>
        (filter === 'all' || w.status === filter) &&
        (!q || w.payeeName?.toLowerCase().includes(q) || w.destination?.accountNumber?.includes(q) || (w.utr || '').toLowerCase().includes(q) || w.id.toLowerCase().includes(q))
    );
  }, [rows, filter, search]);

  const s = rows.find((w) => w.id === selectedId) || null;
  const razorpayx = !!config?.razorpayx;
  const actions = s && isSuperAdmin ? actionsFor(s, razorpayx) : [];

  const copy = (text: string, what: string) => {
    navigator.clipboard?.writeText(text).then(
      () => setToast({ msg: `${what} copied`, severity: 'info' }),
      () => setToast({ msg: `Couldn't copy — select it instead`, severity: 'error' })
    );
  };

  const run = async (action: WithdrawalAction) => {
    if (!s) return;
    setActing(true);
    try {
      const r = await withdrawalAdminService.resolve(s.id, action, action === 'mark_paid' ? { utr: utr.trim(), method, note: note.trim() } : action === 'reject' ? { reason: reason.trim() } : {});
      const msg: Record<WithdrawalAction, string> = {
        approve: `Approved — ${s.payeeName} now sees it as processing`,
        mark_paid: `Marked paid — ${s.payeeName} has been told`,
        reject: `Rejected — ${inr(s.amount)} is back in their wallet`,
        payout: r.status === 'paid' ? 'Paid by RazorpayX' : r.status === 'failed' ? 'RazorpayX could not pay this' : `Sent to RazorpayX (${r.payoutStatus || 'processing'})`,
        refresh: r.note || `RazorpayX says: ${r.payoutStatus || r.status}`,
      };
      setToast({ msg: msg[action], severity: r.status === 'failed' ? 'error' : 'success' });
      setDialog(null);
      setUtr('');
      setNote('');
      setMethod('bank_transfer');
      setReason('');
    } catch (e) {
      setToast({ msg: callableError(e), severity: 'error' });
    } finally {
      setActing(false);
      await load();
    }
  };

  const columns: ResponsiveColumn<AdminWithdrawal>[] = [
    {
      key: 'payee',
      header: 'Payee',
      mobile: 'title',
      render: (w) => (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar sx={{ bgcolor: KIND_COLOR[w.kind] || 'primary.main', width: 36, height: 36 }}>{(w.payeeName || '?').charAt(0).toUpperCase()}</Avatar>
          <Box>
            <Typography fontWeight={700}>{w.payeeName}</Typography>
            <Typography variant="caption" color="text.secondary">{KIND_LABEL[w.kind] || 'Partner'} · {when(w.createdAt)}</Typography>
          </Box>
        </Stack>
      ),
    },
    { key: 'amount', header: 'Amount', align: 'right', mobile: 'subtitle', render: (w) => <Typography fontWeight={800}>{inr(w.amount)}</Typography> },
    { key: 'bank', header: 'To', mobile: 'body', render: (w) => <Typography variant="body2" fontFamily="monospace">•••• {w.destination?.accountNumber?.slice(-4)} · {w.destination?.ifsc}</Typography> },
    { key: 'ref', header: 'Reference', mobile: 'body', render: (w) => w.utr || (w.payoutId ? <Typography variant="caption" fontFamily="monospace">{w.payoutId}</Typography> : '—') },
    { key: 'status', header: 'Status', mobile: 'actions', render: (w) => <StatusChip status={w.status} /> },
  ];

  const detail = (k: string, v: React.ReactNode, copyable?: string) => (
    <Stack key={k} direction="row" spacing={2} alignItems="center" sx={{ py: 0.75 }}>
      <Typography variant="body2" color="text.secondary" sx={{ width: 120, flexShrink: 0 }}>{k}</Typography>
      <Typography variant="body2" sx={{ flex: 1, wordBreak: 'break-all' }} fontFamily={copyable ? 'monospace' : undefined}>{v}</Typography>
      {copyable && (
        <Tooltip title={`Copy ${k.toLowerCase()}`}>
          <IconButton size="small" onClick={() => copy(copyable, k)}><ContentCopy fontSize="small" /></IconButton>
        </Tooltip>
      )}
    </Stack>
  );

  return (
    <Box>
      <PageHeader title="Withdrawals" subtitle="Partners, agencies and customers asking for their money. It is already set aside in their wallet." />

      {config && (
        <Alert severity={razorpayx ? 'success' : 'info'} icon={razorpayx ? <Bolt /> : <AccountBalance />} sx={{ mb: 2 }}>
          {razorpayx
            ? <>RazorpayX is connected — <b>Pay now</b> sends the money automatically.{!config.webhook && ' Add RAZORPAYX_WEBHOOK_SECRET so results arrive by themselves; until then use Refresh.'}</>
            : <>Pay each request yourself by bank transfer or UPI from the business account: <b>Approve</b> it (the partner sees <i>processing</i>), pay it outside the app, then <b>Mark paid</b> with the reference. Rejecting returns the money to their wallet.</>}
          {' '}Minimum withdrawal: <b>{inr(config.minimum)}</b> for partners, <b>{inr(config.customerMinimum)}</b> for customers (change them in Settings).
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}><StatCard label="Waiting to be paid" value={counts.pending || 0} icon={<HourglassTop />} tone="warning" /></Grid>
        <Grid item xs={6} md={3}><StatCard label="Owed in total" value={inr(owed)} icon={<AccountBalance />} tone="primary" /></Grid>
        <Grid item xs={6} md={3}><StatCard label="Need attention" value={counts.failed || 0} icon={<ErrorOutline />} tone="error" /></Grid>
        <Grid item xs={6} md={3}><StatCard label="Paid" value={counts.paid || 0} icon={<CheckCircle />} tone="success" /></Grid>
      </Grid>

      <SectionCard disablePadding>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between" sx={{ p: 2 }}>
          <FilterTabs<Filter>
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'pending', label: 'To pay', count: counts.pending },
              { value: 'processing', label: 'Sending', count: counts.processing },
              { value: 'failed', label: 'Failed', count: counts.failed },
              { value: 'paid', label: 'Paid', count: counts.paid },
              { value: 'rejected', label: 'Rejected', count: counts.rejected },
              { value: 'all', label: 'All', count: rows.length },
            ]}
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <SearchField value={search} onChange={setSearch} placeholder="Search name, account or UTR" />
            <Tooltip title="Reload"><IconButton onClick={load}><Refresh /></IconButton></Tooltip>
          </Stack>
        </Stack>
        <ResponsiveTable<AdminWithdrawal>
          columns={columns}
          rows={shown}
          rowKey={(w) => w.id}
          loading={loading}
          onRowClick={(w) => setSelectedId(w.id)}
          emptyTitle={filter === 'pending' ? 'Nothing waiting to be paid' : 'No withdrawals here'}
          emptyDescription="Partners ask from Earnings in the app, customers from their Wallet."
        />
      </SectionCard>

      <Drawer anchor="right" open={!!s} onClose={() => setSelectedId(null)} PaperProps={{ sx: { width: { xs: '100%', sm: 500 } } }}>
        {s && (
          <Box sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" fontWeight={800}>{inr(s.amount)}</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <StatusChip status={s.status} size="small" />
                  <Typography variant="caption" color="text.secondary">{KIND_LABEL[s.kind] || 'Partner'} · {s.payeeName}</Typography>
                </Stack>
              </Box>
              <IconButton onClick={() => setSelectedId(null)}><Close /></IconButton>
            </Stack>

            {s.status === 'failed' && <Alert severity="error" sx={{ mt: 2 }}>Payout failed{s.lastError ? `: ${s.lastError}` : ''}. The money is still held — pay again, mark paid if you sent it another way, or reject to return it.</Alert>}
            {s.status === 'processing' && <Alert severity="info" sx={{ mt: 2 }}>RazorpayX is sending this ({s.payoutStatus || 'processing'}). It updates by itself; Refresh asks RazorpayX now.{s.lastError ? ` Last error: ${s.lastError}` : ''}</Alert>}
            {s.status === 'rejected' && s.rejectionReason && <Alert severity="warning" sx={{ mt: 2 }}>Rejected: {s.rejectionReason}</Alert>}
            {s.kind === 'customer' && ['pending', 'failed'].includes(s.status) && (
              <Alert severity="info" sx={{ mt: 2 }}>The customer typed this bank account themselves. Check the name matches the customer before paying.</Alert>
            )}
            {!isSuperAdmin && ['pending', 'failed', 'processing'].includes(s.status) && <Alert severity="info" sx={{ mt: 2 }}>Only a super admin can pay or reject withdrawals.</Alert>}

            {actions.length > 0 && (
              <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                {actions.includes('approve') && <Button variant="contained" startIcon={<PlayArrow />} onClick={() => run('approve')} disabled={acting}>Approve &amp; pay</Button>}
                {actions.includes('payout') && <Button variant="contained" startIcon={<Bolt />} onClick={() => setDialog('payout')}>{s.status === 'processing' ? 'Send again' : 'Pay now'}</Button>}
                {actions.includes('mark_paid') && <Button variant={s.manual || !actions.includes('approve') ? 'contained' : 'outlined'} color="success" onClick={() => setDialog('mark_paid')}>Mark paid</Button>}
                {actions.includes('reject') && <Button variant="outlined" color="error" onClick={() => setDialog('reject')}>Reject</Button>}
                {actions.includes('refresh') && <Button variant="outlined" startIcon={<Refresh />} disabled={acting} onClick={() => run('refresh')}>Refresh</Button>}
              </Stack>
            )}

            <Divider sx={{ my: 2.5 }} />
            <Typography variant="overline" color="text.secondary">Pay to</Typography>
            {detail('Account holder', s.destination.accountHolderName, s.destination.accountHolderName)}
            {detail('Account number', s.destination.accountNumber, s.destination.accountNumber)}
            {detail('IFSC', s.destination.ifsc, s.destination.ifsc)}
            {detail('Amount', inr(s.amount), String(s.amount))}

            <Divider sx={{ my: 2.5 }} />
            <Typography variant="overline" color="text.secondary">Details</Typography>
            {detail('Requested', when(s.createdAt))}
            {s.paidAt && detail('Paid', `${when(s.paidAt)}${s.paidVia === 'razorpayx' ? ' · RazorpayX' : s.payMethod ? ` · ${{ bank_transfer: 'bank transfer', upi: 'UPI', other: 'other method' }[s.payMethod]}` : s.paidVia ? ' · bank transfer' : ''}`)}
            {s.paidByName && detail('Paid by', s.paidByName)}
            {s.approvedByName && detail('Approved by', s.approvedByName)}
            {s.paidNote && detail('Note', s.paidNote)}
            {s.utr && detail('UTR', s.utr, s.utr)}
            {s.payoutId && detail('RazorpayX payout', s.payoutId, s.payoutId)}
            {detail('Request ID', s.id, s.id)}

            {s.history?.length > 0 && (
              <>
                <Divider sx={{ my: 2.5 }} />
                <Typography variant="overline" color="text.secondary">History</Typography>
                {[...s.history].reverse().map((h, i) => (
                  <Stack key={i} direction="row" spacing={1.5} sx={{ py: 0.75 }} alignItems="flex-start">
                    <Chip label={h.status} size="small" variant="outlined" sx={{ minWidth: 86 }} />
                    <Box>
                      <Typography variant="body2">{h.note}</Typography>
                      <Typography variant="caption" color="text.secondary">{when(h.at)}{h.by === 'razorpayx' ? ' · RazorpayX' : h.by === s.uid ? ' · payee' : ''}</Typography>
                    </Box>
                  </Stack>
                ))}
              </>
            )}
          </Box>
        )}
      </Drawer>

      <Dialog open={!!dialog && !!s} onClose={() => !acting && setDialog(null)} maxWidth="xs" fullWidth>
        {dialog && s && (
          <>
            <DialogTitle>
              {dialog === 'mark_paid' ? `Mark ${inr(s.amount)} as paid?` : dialog === 'reject' ? `Reject ${inr(s.amount)}?` : `Pay ${inr(s.amount)} now?`}
            </DialogTitle>
            <DialogContent>
              {dialog === 'mark_paid' && (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Only after the transfer to •••• {s.destination.accountNumber.slice(-4)} has gone through. {s.payeeName} is told and sees the reference.
                  </Typography>
                  <TextField select fullWidth label="Paid by" value={method} onChange={(e) => setMethod(e.target.value as PayMethod)} SelectProps={{ native: true }} sx={{ mb: 2 }}>
                    <option value="bank_transfer">Bank transfer (NEFT / IMPS / RTGS)</option>
                    <option value="upi">UPI</option>
                    <option value="other">Other</option>
                  </TextField>
                  <TextField autoFocus fullWidth label="Bank / UPI reference (UTR)" value={utr} onChange={(e) => setUtr(e.target.value.replace(/\s+/g, ''))} required inputProps={{ maxLength: 30 }} helperText="6–30 letters or digits, from your bank statement or UPI app" sx={{ mb: 2 }} />
                  <TextField fullWidth label="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} inputProps={{ maxLength: 200 }} helperText="Kept in the audit trail with your name" />
                </>
              )}
              {dialog === 'reject' && (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>The amount goes back to their wallet straight away.</Typography>
                  <TextField autoFocus fullWidth multiline minRows={2} label="Reason (shown to them)" value={reason} onChange={(e) => setReason(e.target.value)} required />
                </>
              )}
              {dialog === 'payout' && (
                <Typography variant="body2" color="text.secondary">
                  RazorpayX sends {inr(s.amount)} by {s.amount <= 500000 ? 'IMPS' : 'NEFT'} to {s.destination.accountHolderName}, •••• {s.destination.accountNumber.slice(-4)} ({s.destination.ifsc}) from your RazorpayX balance.
                  {s.status === 'processing' ? ' The last send wasn’t confirmed; sending again re-uses the same request, so it can’t pay twice.' : ''}
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialog(null)} disabled={acting}>Cancel</Button>
              <Button
                variant="contained"
                color={dialog === 'reject' ? 'error' : dialog === 'mark_paid' ? 'success' : 'primary'}
                onClick={() => run(dialog)}
                disabled={acting || (dialog === 'mark_paid' && !/^[A-Za-z0-9]{6,30}$/.test(utr)) || (dialog === 'reject' && !reason.trim())}
              >
                {acting ? <CircularProgress size={20} color="inherit" /> : dialog === 'mark_paid' ? 'Mark paid' : dialog === 'reject' ? 'Reject & refund' : 'Pay now'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default Withdrawals;
